import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { Notice } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { soilSessionService, SoilInstruction } from '../../services/soilSessionService';
import { formatMeasure } from '../../utils/formatMeasure';
import './nutrition.css';

const COLS = ['blok', 'beskrywing', 'bome', 'ha', 'werklik', 'datum', 'ec', 'handtekening'] as const;

const SoilInstructionPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const programId = searchParams.get('program');

  const [data, setData] = useState<SoilInstruction | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    // Links back into the workspace must carry ?program=, or the layout falls
    // back to the tenant's ACTIVE programme and returns the user to a board
    // they were never on (#429). This page knows only the SESSION id — the
    // instruction payload carries no soilProgramId — so the caller passes the
    // programme through the URL, and a caller that doesn't degrades to bare.
    { label: t('nutrition.soil.workspace.title'), to: programId ? `/nutrition/soil?program=${programId}` : '/nutrition/soil' },
    { label: t('nutrition.soil.crumb.instruction') },
  ]);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setError('');
      setData(await soilSessionService.getInstruction(id));
    } catch (e: any) {
      const status = e?.response?.status;
      setError(status === 409 ? t('nutrition.soil.instruction.notPlanned')
        : status === 404 ? t('nutrition.soil.instruction.notFound')
        : t('nutrition.soil.instruction.loadError'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { load(); }, [load]);

  const download = async (force: boolean) => {
    if (!id) return;
    setBusy(true);
    try {
      await soilSessionService.downloadInstructionPdf(id, { force });
      await load();
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!id || !window.confirm(t('nutrition.soil.instruction.deleteConfirm'))) return;
    setBusy(true);
    try { await soilSessionService.deleteInstructionPdf(id); await load(); } finally { setBusy(false); }
  };

  // Error takes precedence over the loading guard (brick-on-load lesson S1b/S2).
  if (error) return <div className="pat-page"><Notice tone="danger">{error}</Notice></div>;
  if (loading || !data) return <div className="pat-page">{t('common.loading')}</div>;

  return (
    <div className="pat-page" data-testid="soil-instruction-page">
      <PageHeader
        title={t('nutrition.soil.instruction.title')}
        meta={`${data.session.productName} · ${data.session.month}`}
        actions={
          <>
            <button className="pat-btn pat-btn--primary" disabled={busy} onClick={() => download(false)}>
              {t('nutrition.soil.instruction.download')}
            </button>
            <button className="pat-btn pat-btn--subtle" disabled={busy} onClick={() => download(true)}>
              {t('nutrition.soil.instruction.regenerate')}
            </button>
            <button className="pat-btn pat-btn--subtle" disabled={busy || !data.generated?.exists} onClick={remove}>
              {t('nutrition.soil.instruction.delete')}
            </button>
            <button className="pat-btn pat-btn--subtle" disabled={busy} onClick={() => navigate(-1)}>
              {t('nutrition.soil.instruction.back')}
            </button>
          </>
        }
      />

      <div className="pat-card" style={{ marginBottom: 12 }}>
        <div>{t('nutrition.soil.instruction.season')}: {data.session.season}</div>
        <div>{t('nutrition.soil.instruction.farm')}: {data.farmName}</div>
        <div>{t('nutrition.soil.instruction.product')}: {data.session.productName}</div>
        <div>{t('nutrition.soil.instruction.period')}: {data.period}</div>
        {data.generated?.exists && (
          <div className="ntr-muted" data-testid="generated-status">
            {t('nutrition.soil.instruction.generatedStatus', {
              date: data.generated.generatedAt ? new Date(data.generated.generatedAt).toLocaleString(i18n.language) : '',
              lang: data.generated.lang,
            })}
          </div>
        )}
      </div>

      <table className="pat-table">
        <thead>
          <tr>{COLS.map((c) => <th key={c}>{t(`nutrition.soil.instruction.col.${c}`)}</th>)}</tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i} data-testid={`instruction-row-${i}`}>
              <td>{r.orchards.join(', ')}</td>
              <td>{r.cultivars.join(', ')}</td>
              <td>{Math.round(r.treeCount)}</td>
              <td>{formatMeasure(r.areaHa)}</td>
              <td>{formatMeasure(r.plannedKg)}</td>
              <td /><td /><td />
            </tr>
          ))}
          <tr className="pat-table__total" data-testid="instruction-total">
            <td>{t('nutrition.soil.instruction.total')}</td>
            <td />
            <td>{Math.round(data.grandTotal.treeCount)}</td>
            <td>{formatMeasure(data.grandTotal.areaHa)}</td>
            <td>{formatMeasure(data.grandTotal.plannedKg)}</td>
            <td /><td /><td />
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default SoilInstructionPage;
